import { NgClass } from '@angular/common';
import { Component, computed, inject, input, output } from '@angular/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { catchError, map, of, switchMap, take } from 'rxjs';
import { FirestoreService } from '../../../../services/firestore.service';
import {
    AdyenActivationStatus,
    VerificationChecks,
    VerificationStage,
} from '../../../../shared/models/organization-data.model';

@Component({
    imports: [NgClass, MatCheckboxModule, MatTooltipModule],
    selector: 'app-verification-checks',
    templateUrl: './verification-checks.component.html',
    styleUrl: './verification-checks.component.scss',
})
export class VerificationChecksComponent {
    public readonly orgId = input.required<string>();
    public readonly verificationChecks = input<VerificationChecks | undefined>(undefined);
    public readonly disabled = input<boolean>(false);
    public readonly status = input<string | undefined>(undefined);
    public readonly verificationStage = input<VerificationStage | undefined>(undefined);
    public readonly adyenActivationStatus = input<AdyenActivationStatus | undefined>(undefined);
    public readonly adyenActivationError = input<string | null | undefined>(undefined);

    private readonly firestoreService = inject(FirestoreService);
    private readonly snackBar = inject(MatSnackBar);
    public readonly verificationChange = output<VerificationChecks>();

    protected readonly adyenTooltip = computed(() => {
        switch (this.adyenActivationStatus()) {
            case 'success':
                return 'Adyen URLs whitelisted';
            case 'pending':
                return 'Registering Adyen URLs…';
            case 'error':
                return this.adyenActivationError() || 'Adyen activation failed';
            default:
                return 'Preparing Adyen activation…';
        }
    });

    // Adyen can be checked by the backend activation result or manually.
    protected readonly isAdyenChecked = computed(
        () => this.adyenActivationStatus() === 'success' || !!this.verificationChecks()?.adyen
    );

    protected readonly isComplete = computed(() => {
        const checks = this.verificationChecks();
        if (!checks) return false;
        return !!(checks.keycloak && checks.cors && this.isAdyenChecked() && checks.devops && checks.qa);
    });

    protected readonly badgeLabel = computed(() => {
        if (this.isComplete()) return 'Ready';
        // Orgs predating the verificationStage field default to 'Pending Production'.
        return this.verificationStage() === 'pending' ? 'Pending' : 'Pending Production';
    });

    protected isCheckboxDisabled(field: keyof VerificationChecks): boolean {
        if (this.disabled() || this.status() === 'disabled') return true;
        // Adyen locks once activated by the backend or checked manually.
        if (field === 'adyen') return this.isAdyenChecked();
        // Once a check is confirmed it cannot be unchecked.
        return !!this.verificationChecks()?.[field];
    }

    protected toggle(field: keyof VerificationChecks, checked: boolean): void {
        if (!checked) return;
        const current = this.verificationChecks() ?? {};
        const updated: VerificationChecks = { ...current, [field]: checked };
        this.firestoreService
            .updateOrganization(this.orgId(), { verificationChecks: updated })
            .pipe(
                take(1),
                switchMap(() => {
                    const allTrue = !!(
                        updated.keycloak &&
                        updated.cors &&
                        (this.adyenActivationStatus() === 'success' || updated.adyen) &&
                        updated.devops &&
                        updated.qa
                    );
                    if (allTrue) {
                        return this.firestoreService.notifyReadyToShare(this.orgId()).pipe(
                            take(1),
                            map(() => true),
                            catchError(() => of(false))
                        );
                    }
                    return of(false);
                })
            )
            .subscribe({
                next: (notificationSend) => {
                    this.verificationChange.emit(updated);
                    if (notificationSend) {
                        this.snackBar.open('Ready notification sent to sales', 'Close', {
                            duration: 3000,
                        });
                    }
                },
                error: () => {
                    this.snackBar.open('Error updating verification status', 'Close', {
                        duration: 3000,
                    });
                },
            });
    }
}
